/**
 * Pilot accept → SimConnect phase machine on the canonical ticket.
 *
 * Assigned → Accepted → Taxi → Airborne → Arrived (or Diverted) → Completed
 * after ~5 minutes parked at dest. Cancelled always wins. Diverted does not
 * auto-complete. Last parking field stays — no teleport home.
 */
import { assignmentForMission, type TripAssignment } from "./assignment";
import { resolveCoords } from "./airports";
import { phaseIsAirborne, phaseIsArrived, type FlightPhaseId } from "./flightPhase";
import { haversineNm } from "./geo";
import { isTicketLifecycle } from "./opsBoard";
import { stampSimAtd } from "./ticketAtd";
import type { TicketLifecycle } from "./types";
import type { FusedPosition } from "./position";
import type { Mission, MissionStatus, SceneryAirport } from "./types";

/** Parked at dest this long after Arrived → Completed. Emerson: 1–5 min; lock 5. */
export const PARKED_COMPLETE_MS = 5 * 60_000;

/** Treat landing within this NM of dest as dest arrival. */
export const DEST_ARRIVAL_NM = 8;

export type LifecycleAdvance = {
  missionId: string;
  lifecycle: TicketLifecycle;
  missionStatus: MissionStatus;
  diverted: boolean;
  divertToIcao?: string;
  divertReason?: string;
  arrivedAtUtc?: string;
  notice: string;
};

export type AdvanceFlightLifecycleInput = {
  mission: Mission;
  assignments?: TripAssignment[];
  phase?: FlightPhaseId | null;
  fromSim?: boolean;
  fused?: FusedPosition | null;
  scenery?: SceneryAirport[];
  nearestIcao?: string | null;
  now?: Date;
};

function icao(value?: string | null): string {
  return (value ?? "").trim().toUpperCase();
}

function landedAtDest(input: AdvanceFlightLifecycleInput): boolean {
  const dest = icao(input.mission.destIcao);
  if (!dest) return false;
  const near = icao(input.nearestIcao);
  if (near && near === dest) return true;
  const fused = input.fused;
  const scenery = input.scenery ?? [];
  if (!fused || !scenery.length) return near === dest;
  const coords = resolveCoords(dest, scenery);
  if (!coords) return near === dest;
  const nm = haversineNm(fused.lat, fused.lon, coords.lat, coords.lon);
  return Number.isFinite(nm) && nm <= DEST_ARRIVAL_NM;
}

export function shouldAutoCompleteArrived(input: {
  lifecycle: TicketLifecycle;
  arrivedAtUtc?: string | null;
  now: Date;
}): boolean {
  if (input.lifecycle !== "arrived") return false;
  const t = input.arrivedAtUtc ? Date.parse(input.arrivedAtUtc) : NaN;
  if (!Number.isFinite(t)) return false;
  return input.now.getTime() - t >= PARKED_COMPLETE_MS;
}

export function applyLifecycleAdvance(mission: Mission, advance: LifecycleAdvance, now = new Date()): Mission {
  if (mission.status === "completed" || mission.lifecycle === "completed" || mission.status === "cancelled") {
    return mission;
  }
  const iso = now.toISOString();
  const dest = icao(mission.destIcao);
  const next: Mission = {
    ...mission,
    lifecycle: advance.lifecycle,
    status: advance.missionStatus,
    acceptedAt: mission.acceptedAt ?? iso,
    startedAt:
      advance.lifecycle === "airborne" ||
      advance.lifecycle === "taxi" ||
      advance.lifecycle === "diverting" ||
      advance.lifecycle === "diverted"
        ? (mission.startedAt ?? iso)
        : mission.startedAt,
    arrivedAtUtc: advance.arrivedAtUtc ?? mission.arrivedAtUtc,
    completedAt: advance.missionStatus === "completed" ? iso : mission.completedAt,
  };
  if (advance.lifecycle === "airborne") {
    const atd = stampSimAtd(mission, { airborneUtc: iso });
    if (atd) {
      next.actualDepartureUtc = atd.actualDepartureUtc;
      next.actualDepartureSource = atd.actualDepartureSource;
    }
  }
  if (advance.lifecycle === "diverting" || advance.lifecycle === "diverted") {
    const to = icao(advance.divertToIcao) || icao(mission.divertToIcao);
    if (to) next.divertToIcao = to;
    const reason = (advance.divertReason ?? mission.divertReason)?.trim();
    if (reason) next.divertReason = reason;
  }
  if (advance.lifecycle === "arrived" && dest) {
    next.divertToIcao = undefined;
    next.divertReason = undefined;
  }
  return next;
}

/** Stored ticket phase only — assignment accept is inferred for readers, then persisted here. */
function storedLifecycle(mission: Mission): TicketLifecycle {
  if (mission.status === "cancelled") return "cancelled";
  if (mission.status === "completed") return "completed";
  if (isTicketLifecycle(mission.lifecycle)) return mission.lifecycle;
  if (mission.status === "in-progress") return mission.activeBoundAt ? "active" : "accepted";
  return "scheduled";
}

export function advanceFlightLifecycle(input: AdvanceFlightLifecycleInput): LifecycleAdvance | null {
  const mission = input.mission;
  const now = input.now ?? new Date();
  const assignment = assignmentForMission(input.assignments ?? [], mission.id);
  const current = storedLifecycle(mission);
  if (current === "cancelled" || current === "completed") return null;

  const phase = input.phase ?? "no_sim";
  const live = Boolean(input.fromSim);

  if (assignment?.status === "accepted" && current === "scheduled") {
    return {
      missionId: mission.id,
      lifecycle: "accepted",
      missionStatus: "pending",
      diverted: false,
      notice: `ACCEPTED  ${mission.callsign}`,
    };
  }

  if (!live) return null;

  if (phase === "taxi" && (current === "accepted" || current === "active" || current === "scheduled")) {
    return {
      missionId: mission.id,
      lifecycle: "taxi",
      missionStatus: "in-progress",
      diverted: false,
      notice: `TAXI  ${mission.callsign}`,
    };
  }

  if (phase === "takeoff" || phaseIsAirborne(phase)) {
    if (current === "diverting" || current === "diverted") return null;
    if (current !== "airborne") {
      return {
        missionId: mission.id,
        lifecycle: "airborne",
        missionStatus: "in-progress",
        diverted: false,
        notice: `AIRBORNE  ${mission.callsign}`,
      };
    }
    return null;
  }

  if (phaseIsArrived(phase) && (current === "airborne" || current === "taxi" || current === "diverting")) {
    const iso = now.toISOString();
    if (landedAtDest(input)) {
      return {
        missionId: mission.id,
        lifecycle: "arrived",
        missionStatus: "in-progress",
        diverted: false,
        arrivedAtUtc: mission.arrivedAtUtc ?? iso,
        notice: `ARRIVED  ${mission.callsign} ${mission.destIcao}`,
      };
    }
    const divertTo = icao(input.nearestIcao) || icao(mission.divertToIcao);
    return {
      missionId: mission.id,
      lifecycle: "diverted",
      missionStatus: "in-progress",
      diverted: true,
      divertToIcao: divertTo || undefined,
      divertReason: mission.divertReason,
      arrivedAtUtc: mission.arrivedAtUtc ?? iso,
      notice: divertTo
        ? `DIVERTED  ${mission.callsign} ${divertTo} — not ${mission.destIcao}`
        : `DIVERTED  ${mission.callsign} — not ${mission.destIcao}`,
    };
  }

  if (
    current === "arrived" &&
    phase === "parked" &&
    shouldAutoCompleteArrived({ lifecycle: current, arrivedAtUtc: mission.arrivedAtUtc, now })
  ) {
    return {
      missionId: mission.id,
      lifecycle: "completed",
      missionStatus: "completed",
      diverted: false,
      notice: `COMPLETE  ${mission.callsign}`,
    };
  }

  return null;
}
